import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login({ email, password });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-6 py-20 md:py-28">
      <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">Members</div>
      <h1 className="font-serif font-black text-4xl md:text-5xl leading-[1.02] text-sand-900 mb-8">
        Welcome back.
      </h1>

      <button
        onClick={googleLogin}
        data-testid="google-login-button"
        className="w-full border border-sand-900 px-5 py-4 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-sand-900 hover:bg-sand-900 hover:text-sand-50 transition-colors flex items-center justify-center gap-3"
      >
        Continue with Google
      </button>

      <div className="flex items-center gap-4 my-8">
        <div className="h-px flex-1 bg-sand-200" />
        <span className="font-mono text-[10px] tracking-widest uppercase text-sand-600">or</span>
        <div className="h-px flex-1 bg-sand-200" />
      </div>

      <form onSubmit={submit} className="space-y-5">
        {error && (
          <div data-testid="login-error" className="bg-brand-50 border border-brand-500 text-brand-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        <div>
          <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-sand-700 mb-2">Email</label>
          <input
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-sand-300 px-4 py-3 bg-white focus:border-brand-500 outline-none"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-sand-700 mb-2">Password</label>
          <input
            data-testid="login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-sand-300 px-4 py-3 bg-white focus:border-brand-500 outline-none"
          />
        </div>
        <button
          data-testid="login-submit-button"
          disabled={busy}
          className="w-full bg-sand-900 text-sand-50 px-5 py-4 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>

      <p className="mt-8 text-sm text-sand-700">
        New to Tastebuds?{" "}
        <Link to="/register" data-testid="go-to-register" className="text-brand-500 editorial-link font-medium">
          Create an account
        </Link>
      </p>
    </div>
  );
}
